import React from 'react';
import { Radio } from 'antd';
import { FP610 } from '../../../services/pwp254Api';
import { dateToInput, inputToDate, fmt1 } from '../helpers';

interface Tab1Props {
  formData: Partial<FP610>;
  names: Record<string, string>;
  isEditing: boolean;
  getField: (key: keyof FP610) => FP610[keyof FP610];
  setField: (key: keyof FP610, value: FP610[keyof FP610]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, getField, setField, resolveSingleName,
}) => {
  const showKnitting = (formData.fp610_27 || 0) !== 3;

  return (
    <div className="tab-pane active">
      {/* Section: S/C & Product */}
      <div className="form-section odd">
        <div className="form-grid">
          <label>S/C NO</label>
          <input type="text" value={String(getField('fp610_10') || '')} maxLength={6}
            disabled={!isEditing}
            onChange={e => setField('fp610_10', e.target.value.toUpperCase())} />

          <label>產品代號</label>
          <div className="lookup-field">
            <input type="text" value={String(getField('fp610_29') || '')} maxLength={12}
              disabled={!isEditing}
              onChange={e => setField('fp610_29', e.target.value)}
              onBlur={() => resolveSingleName('product', 'product', String(getField('fp610_29') || ''))}
              style={{ width: 120, flex: '0 0 auto' }} />
            <span className="lookup-name">{names.product || ''}</span>
          </div>

          <label>日期</label>
          <input type="date" value={dateToInput(getField('fp610_11') as string | null)}
            disabled={!isEditing}
            onChange={e => setField('fp610_11', inputToDate(e.target.value))} />
        </div>
      </div>

      {/* Section: SHIPPING MARK */}
      <div className="form-section even">
        <div className="form-grid two-col">
          <label>SHIPPING MARK</label>
          <textarea value={String(getField('fp610_13') || '')} rows={3}
            disabled={!isEditing}
            onChange={e => setField('fp610_13', e.target.value)} />
        </div>
      </div>

      {/* Section: DESCRIPTION OF GOODS */}
      <div className="form-section odd">
        <div className="form-grid two-col">
          <label>DESCRIPTION OF GOODS</label>
          <textarea value={String(getField('fp610_141') || '')} rows={3}
            disabled={!isEditing}
            onChange={e => setField('fp610_141', e.target.value)} />
        </div>
      </div>

      {/* Section: Shipping / Customer */}
      <div className="form-section even">
        <div className="form-grid">
          <label>材積</label>
          <input type="text" value={String(getField('fp610_15') || '')} maxLength={50}
            disabled={!isEditing}
            onChange={e => setField('fp610_15', e.target.value)} />

          <label>INVOICE NO.</label>
          <input type="text" value={String(getField('fp610_12') || '')}
            disabled={!isEditing}
            onChange={e => setField('fp610_12', e.target.value)} />

          <label>客戶代號</label>
          <div className="lookup-field">
            <input type="text" value={String(getField('fp610_25') || '')} maxLength={12}
              disabled={!isEditing}
              onChange={e => setField('fp610_25', e.target.value)}
              onBlur={() => resolveSingleName('customer', 'customer', String(getField('fp610_25') || ''))}
              style={{ width: 120, flex: '0 0 auto' }} />
            <span className="lookup-name">{names.customer || ''}</span>
          </div>
        </div>
      </div>

      {/* Section: Options */}
      <div className="form-section odd">
        <div className="form-grid">
          <label>包裝單位</label>
          <Radio.Group value={formData.fp610_21 || 0} disabled={!isEditing}
            onChange={e => setField('fp610_21', e.target.value)}>
            <Radio value={1}>R/NO.</Radio>
            <Radio value={2}>B/NO.</Radio>
            <Radio value={3}>C/NO.</Radio>
          </Radio.Group>

          <label>內外銷別</label>
          <Radio.Group value={formData.fp610_26 || 0} disabled={!isEditing}
            onChange={e => setField('fp610_26', e.target.value)}>
            <Radio value={1}>外銷</Radio>
            <Radio value={2}>內銷</Radio>
          </Radio.Group>

          <label>出貨單位</label>
          <Radio.Group value={formData.fp610_27 || 0} disabled={!isEditing}
            onChange={e => setField('fp610_27', e.target.value)}>
            <Radio value={1}>1</Radio>
            <Radio value={2}>2</Radio>
            <Radio value={3}>3</Radio>
          </Radio.Group>

          {showKnitting && (
            <>
              <label>針織/平織</label>
              <Radio.Group value={formData.fp610_28 || 0} disabled={!isEditing}
                onChange={e => setField('fp610_28', e.target.value)}>
                <Radio value={1}>針織</Radio>
                <Radio value={2}>平織</Radio>
              </Radio.Group>
            </>
          )}
        </div>
      </div>

      {/* Section: Totals (readonly) */}
      <div className="form-section even">
        <div className="form-grid">
          <label>總碼數</label>
          <input type="text" value={fmt1(formData.fp610_17)} readOnly disabled />

          <label>總米數</label>
          <input type="text" value={fmt1(formData.fp610_18)} readOnly disabled />

          <label>總重量</label>
          <input type="text" value={fmt1(formData.fp610_19)} readOnly disabled />

          <label>總淨量</label>
          <input type="text" value={fmt1(formData.fp610_20)} readOnly disabled />
        </div>
      </div>

      {/* Section: Custom fields */}
      <div className="form-section odd">
        <div className="form-grid">
          <label>自訂1</label>
          <input type="text" value={String(getField('fp610_31') || '')}
            disabled={!isEditing}
            onChange={e => setField('fp610_31', e.target.value)} />

          <label>自訂2</label>
          <input type="text" value={String(getField('fp610_32') || '')}
            disabled={!isEditing}
            onChange={e => setField('fp610_32', e.target.value)} />

          <label>自訂3</label>
          <input type="text" value={String(getField('fp610_33') || '')}
            disabled={!isEditing}
            onChange={e => setField('fp610_33', e.target.value)} />
        </div>
      </div>
    </div>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
